/**
 * NNADD Configurador — o desenho da peça configurada (U5).
 *
 * Um só módulo para a página (pré-visualização) e para `tools/coordinates`
 * (ficheiro impresso), para que o que o cliente aprova seja o que é impresso
 * (R3). Sem imports (KTD1): as dependências chegam num objeto injetado,
 *
 *   criarDesenho({ mapas, glifos, formatarDMS, logotipo })
 *
 * - `mapas`: o módulo `assets/nnadd-mapas.js` (U3);
 * - `glifos`: o módulo `assets/nnadd-configurador-glifos.js` (U2), que tem
 *   também os contornos de ° ′ ″ (`CONTORNOS_SINAIS`) para as coordenadas;
 * - `formatarDMS`: de `assets/nnadd-coordinates-design.js`;
 * - `logotipo`: `LOGOTIPO` de `assets/nnadd-coordinates-design.js`
 *   (`{ viewBox, conteudo }`), o logo oficial que vai sempre no peito, por
 *   baixo do desenho (R12). Desenha-se na tinta da peça: as cores próprias do
 *   SVG (fill, stroke, style) tiram-se.
 *
 * O que fica no topo do módulo (sem dependências): a tabela de peças
 * (`PECAS`, KTD9), a lista de peças ativas (`PECAS_ATIVAS`, KTD8), as tintas,
 * as composições, as disposições, `temCostas`, `tintasPermitidas`,
 * `tintaPorOmissao`, `transicaoPeca` (AE1) e `mudarCor`. `criarDesenho` devolve ainda
 * `validarDescritor`, `pedidoMapa`, `pedidoDados`, `formaMapa`, `ladosComMapa`,
 * `descritorDoLado`, `desenharLados` e `desenharPeca`.
 *
 * ===== O descritor (KTD4)
 *
 * `{ versao?, disposicao?, peca, cor, tamanho, tinta, composicao, estilo, estiloCostas?, pontos, frase, qr }`:
 * - `versao`: uma de `VERSOES_ACEITES`; conf-2 só conhece "frente";
 * - `disposicao`: uma de `DISPOSICOES` (em falta = 'frente'), o que vai em
 *   cada lado (plano de 2026-10-22, KTD1):
 *   - 'frente': o desenho de hoje à frente, com o logo; nada atrás;
 *   - 'frente_pequena_costas': à frente as coordenadas e o logo, pequenos
 *     (`MEDIDAS.frentePequenaLarguraMm`); atrás o mapa e a frase. Exige a
 *     composição 'mapa' (o que vai atrás) e um estilo;
 *   - 'costas': à frente só o logo; atrás o desenho de hoje sem o logo;
 *   - 'frente_costas': à frente o desenho de hoje (composição com mapa) com o
 *     logo; atrás só o mapa dos mesmos lugares no estilo `estiloCostas`;
 * - `estiloCostas`: só em 'frente_costas', diferente de `estilo`;
 * - `peca`: chave de `PECAS`; `cor`: valor da opção Color da Shopify;
 *   `tamanho`: um de `TAMANHOS`; `tinta`: chave de `TINTAS`;
 * - `composicao`: um de `COMPOSICOES`; `estilo`: um estilo de mapa (U3) nas
 *   composições com mapa, `null` em 'coordenadas';
 * - `pontos`: `[{ lat, lng }]`, dois em 'duas_terras' (origem primeiro);
 * - `nomes`: só em 'duas_terras', `[origem, destino]`, os nomes dos lugares
 *   como o desenho os escreve (`nomeParaDesenho`: maiúsculas, só a letra da
 *   frase, até 40 caracteres; vazio = o lugar fica só com as coordenadas).
 *   Na encomenda vêm de `Lugar` e `Lugar 2`;
 * - `frase`: texto (vazio = sem frase); `qr`: booleano.
 *
 * `VERSAO_CONFIGURADOR` vai para a encomenda. Mudar sempre que o desenho
 * impresso mudar (medidas, composição, letra).
 */

export const VERSAO_CONFIGURADOR = 'conf-3';
/** As versões que o desenho aceita (KTD2): conf-2 lê-se como 'frente'. */
/**
 * O mapa de um lado enquanto os dados não chegam: em vez de `mapas.desenharMapa(...)`, desenha a
 * forma do mapa cheia, esbatida, com `data-camada="mapa-espera"` (a página fá-la pulsar). Só para a
 * pré-visualização: o PC nunca o recebe, e o desenho com o mapa a sério não muda.
 */
export const MAPA_EM_ESPERA = Object.freeze({ espera: true });

export const VERSOES_ACEITES = Object.freeze(['conf-2', VERSAO_CONFIGURADOR]);

const congelar = (o) => Object.freeze(o);
const dono = (o, k) => typeof k === 'string' && o != null && Object.prototype.hasOwnProperty.call(o, k);

// ===== TINTAS: as mesmas quatro da Coordinates (os hex são verificados nos testes).
export const TINTAS = congelar({
  branco: congelar({ hex: '#FFFFFF', etiquetas: congelar({ 'pt-PT': 'Branco', en: 'White', fr: 'Blanc', de: 'Weiß' }) }),
  preto: congelar({ hex: '#000000', etiquetas: congelar({ 'pt-PT': 'Preto', en: 'Black', fr: 'Noir', de: 'Schwarz' }) }),
  dourado: congelar({ hex: '#C9A24A', etiquetas: congelar({ 'pt-PT': 'Dourado', en: 'Gold', fr: 'Doré', de: 'Gold' }) }),
  bordeaux: congelar({ hex: '#6B1E2E', etiquetas: congelar({ 'pt-PT': 'Bordeaux', en: 'Burgundy', fr: 'Bordeaux', de: 'Bordeaux' }) }),
});

// A regra da Coordinates: peças escuras levam branco ou dourado, claras preto
// ou bordeaux. A primeira tinta é a que vem selecionada.
const TINTAS_ESCURA = congelar(['branco', 'dourado']);
const TINTAS_CLARA = congelar(['preto', 'bordeaux']);

export const TAMANHOS = congelar(['S', 'M', 'L', 'XL']);

/** As composições (R7). */
export const COMPOSICOES = congelar(['coordenadas', 'coordenadas_mapa', 'mapa']);
const COM_MAPA = congelar(['coordenadas_mapa', 'mapa']);

/** As disposições (R1), pela ordem da página. A primeira é a por omissão. */
export const DISPOSICOES = congelar(['frente', 'frente_pequena_costas', 'costas', 'frente_costas']);

/**
 * Se a disposição leva alguma coisa nas costas (e por isso a variante "+ costas", KTD3); em falta é
 * 'frente'. A mesma regra na página, no estado e no PC.
 */
export function temCostas(disposicao) {
  return (disposicao ?? 'frente') !== 'frente';
}

// ===== PEÇAS (KTD9). Valores da Fase 0 (U1) e da API v2 do Printful, lidos a
// 2026-10-20 (catalog_variant_id por tamanho). As chaves de `cores` são o
// valor da opção Color da Shopify; `printfulCor` é o nome da cor no Printful.
const cor = (hex, tom, printfulCor, printful) =>
  congelar({ hex, tom, printfulCor, printful: congelar(printful), tintas: tom === 'escura' ? TINTAS_ESCURA : TINTAS_CLARA });

// A área da frente da Coordinates (12 × 16 polegadas). Na hoodie e na
// sweatshirt fica provisória até U9 confirmar no Printful.
const area = (aConfirmar) =>
  congelar({ larguraPol: 12, alturaPol: 16, larguraMm: 304.8, alturaMm: 406.4, aConfirmar });
// As costas (KTD7): placement `back` em DTG, 12 × 16 in, com o topo por peça
// (3 in na t-shirt e na sweatshirt; 2,5 in na hoodie, escolhido pelo Bruno a
// 2026-10-22 depois de comparar 2,5, 3,5 e 5 in nos mockups de costas).
// Provisórios até à primeira amostra.
const costas = (topoPol) =>
  ({ placementCostas: 'back', areaCostas: area('provas de costas'), topoCostasPol: topoPol });

/** Os nomes das cores por língua (as chaves são o valor Color da Shopify). */
export const ETIQUETAS_CORES = congelar({
  Black: congelar({ 'pt-PT': 'Preta', en: 'Black', fr: 'Noir', de: 'Schwarz' }),
  White: congelar({ 'pt-PT': 'Branca', en: 'White', fr: 'Blanc', de: 'Weiß' }),
  Sand: congelar({ 'pt-PT': 'Areia', en: 'Sand', fr: 'Sable', de: 'Sand' }),
  Navy: congelar({ 'pt-PT': 'Azul-marinho', en: 'Navy', fr: 'Marine', de: 'Marineblau' }),
  'Military Green': congelar({ 'pt-PT': 'Verde-militar', en: 'Military green', fr: 'Vert militaire', de: 'Militärgrün' }),
});

export const PECAS = congelar({
  tshirt: congelar({
    etiquetas: congelar({ 'pt-PT': 'T-shirt', en: 'T-shirt', fr: 'T-shirt', de: 'T-Shirt' }),
    catalogo: 12,
    modelo: 'Gildan 64000',
    tecnica: 'dtg',
    placementFrente: 'front',
    placementEtiqueta: 'label_inside',
    area: area(null),
    ...costas(3),
    aceitaQR: true,
    limiteFrase: 30,
    // Os mesmos ids e cores de CAMISOLAS em assets/nnadd-coordinates-design.js.
    cores: congelar({
      Black: cor('#0e0e0e', 'escura', 'Black', { S: 474, M: 505, L: 536, XL: 567 }),
      White: cor('#ffffff', 'clara', 'White', { S: 473, M: 504, L: 535, XL: 566 }),
      Sand: cor('#d8c5a9', 'clara', 'Sand', { S: 501, M: 532, L: 563, XL: 594 }),
      Navy: cor('#0f1830', 'escura', 'Navy', { S: 496, M: 527, L: 558, XL: 589 }),
      'Military Green': cor('#686f54', 'escura', 'Military Green', { S: 495, M: 526, L: 557, XL: 588 }),
    }),
  }),
  hoodie: congelar({
    etiquetas: congelar({ 'pt-PT': 'Hoodie', en: 'Hoodie', fr: 'Hoodie', de: 'Hoodie' }),
    catalogo: 380,
    modelo: 'Cotton Heritage M2580',
    tecnica: 'dtg',
    placementFrente: 'front',
    placementEtiqueta: 'label_inside',
    area: area('U9'),
    ...costas(2.5),
    aceitaQR: true,
    limiteFrase: 30,
    cores: congelar({
      Black: cor('#080808', 'escura', 'Black', { S: 10779, M: 10780, L: 10781, XL: 10782 }),
      White: cor('#ffffff', 'clara', 'White', { S: 10774, M: 10775, L: 10776, XL: 10777 }),
      Sand: cor('#f5e8ce', 'clara', 'Bone', { S: 20284, M: 20285, L: 20286, XL: 20287 }),
      Navy: cor('#171f2c', 'escura', 'Navy Blazer', { S: 11491, M: 11492, L: 11493, XL: 11494 }),
      'Military Green': cor('#5c4f32', 'escura', 'Military Green', { S: 13893, M: 13894, L: 13895, XL: 13896 }),
    }),
  }),
  sweatshirt: congelar({
    etiquetas: congelar({ 'pt-PT': 'Sweatshirt', en: 'Sweatshirt', fr: 'Sweat-shirt', de: 'Sweatshirt' }),
    catalogo: 145,
    modelo: 'Gildan 18000',
    tecnica: 'dtg',
    placementFrente: 'front',
    // Só há etiqueta interior em DTF (Fase 0); U9 confirma se o pedido aceita.
    placementEtiqueta: 'label_inside_dtf',
    area: area('U9'),
    ...costas(3),
    aceitaQR: true,
    limiteFrase: 30,
    cores: congelar({
      Black: cor('#0b0b0b', 'escura', 'Black', { S: 5434, M: 5435, L: 5436, XL: 5437 }),
      White: cor('#ffffff', 'clara', 'White', { S: 5426, M: 5427, L: 5428, XL: 5429 }),
      Sand: cor('#e7d3b3', 'clara', 'Sand', { S: 16876, M: 16877, L: 16878, XL: 16879 }),
      Navy: cor('#050c1d', 'escura', 'Navy', { S: 5498, M: 5499, L: 5500, XL: 5501 }),
      'Military Green': cor('#7e8560', 'escura', 'Military Green', { S: 13005, M: 13006, L: 13007, XL: 13008 }),
    }),
  }),
});

/** As peças que a página mostra e o PC aceita (KTD8). Muda quando o Bruno aprova uma amostra. */
export const PECAS_ATIVAS = congelar(['tshirt', 'hoodie', 'sweatshirt']);

/** As tintas permitidas para a cor da peça, pela ordem da escolha (vazio se não existir). */
export function tintasPermitidas(peca, corPeca, pecas = PECAS) {
  const p = dono(pecas, peca) ? pecas[peca] : null;
  return p && dono(p.cores, corPeca) ? [...p.cores[corPeca].tintas] : [];
}

/** A tinta que vem selecionada (a primeira permitida), ou null. */
export function tintaPorOmissao(peca, corPeca, pecas = PECAS) {
  return tintasPermitidas(peca, corPeca, pecas)[0] ?? null;
}

// ===== Transições (AE1)

/**
 * Muda a cor e diz o que aconteceu à tinta: mantém-se se continuar permitida,
 * senão passa para a por omissão, com aviso. Devolve `{ estado, avisos }`.
 */
export function mudarCor(estado, novaCor, pecas = PECAS) {
  const permitidas = tintasPermitidas(estado.peca, novaCor, pecas);
  if (!permitidas.length) throw new Error(`Cor desconhecida para ${estado.peca}: ${JSON.stringify(novaCor)}`);
  const avisos = [];
  let tinta = estado.tinta;
  if (!permitidas.includes(tinta)) {
    tinta = permitidas[0];
    avisos.push({ campo: 'tinta', motivo: 'reposta' });
  }
  return { estado: { ...estado, cor: novaCor, tinta }, avisos };
}

/**
 * Muda de peça (AE1). Lugar, composição, estilo, frase, QR e tamanho mantêm-se.
 * A cor e a tinta de cada peça ficam em `estado.memoria`, para que ao voltar a
 * uma peça regressem as que lá estavam. Sem memória, a cor atual mantém-se se
 * a peça a tiver, senão passa para a primeira cor da peça (aviso); a tinta
 * mantém-se se for permitida, senão passa para a por omissão (aviso).
 * Devolve `{ estado, avisos }`, com `avisos` `[{ campo, motivo: 'reposta' }]`.
 */
export function transicaoPeca(estado, novaPeca, pecas = PECAS) {
  if (!dono(pecas, novaPeca)) throw new Error(`Peça desconhecida: ${JSON.stringify(novaPeca)}`);
  if (estado.peca === novaPeca) return { estado: { ...estado }, avisos: [] };
  const memoria = { ...(estado.memoria ?? {}) };
  if (dono(pecas, estado.peca)) memoria[estado.peca] = { cor: estado.cor, tinta: estado.tinta };
  const cores = pecas[novaPeca].cores;
  const lembrada = memoria[novaPeca];
  const avisos = [];

  let novaCor;
  let tintaPreferida;
  if (lembrada && dono(cores, lembrada.cor)) {
    novaCor = lembrada.cor;
    tintaPreferida = lembrada.tinta;
  } else if (dono(cores, estado.cor)) {
    novaCor = estado.cor;
    tintaPreferida = estado.tinta;
  } else {
    novaCor = Object.keys(cores)[0];
    tintaPreferida = estado.tinta;
    avisos.push({ campo: 'cor', motivo: 'reposta' });
  }
  const permitidas = [...cores[novaCor].tintas];
  let tinta = tintaPreferida;
  if (!permitidas.includes(tinta)) {
    tinta = permitidas[0];
    avisos.push({ campo: 'tinta', motivo: 'reposta' });
  }
  return { estado: { ...estado, peca: novaPeca, cor: novaCor, tinta, memoria }, avisos };
}

// ===== Medidas do desenho, em milímetros (R14: fixas, o cliente não as muda).
// Proporções dos protótipos de 2026-10-20 (~/Documents/nnadd/propostas/minho-2026-10-20).
const MAPA_CIRCULO_MM = congelar({ mapa: 200, coordenadas_mapa: 160 });
// "As duas terras" (KTD5): a moldura sai dos dois pontos (`mapas.formaDuasTerras`:
// 120 mm de altura, 160 a 240 mm de largura) e é a mesma nas duas composições,
// para a costa pedida servir às duas (as coordenadas ficam junto das pontas).
// Sem pontos, a moldura maior.
const MAPA_DUAS_TERRAS_MM = congelar({ larguraMm: 240, alturaMm: 120 });
/** Os nomes de "as duas terras" ficam com, no máximo, este número de caracteres. */
export const NOME_MAX = 40;
/**
 * As medidas do texto e dos espaços, exportadas para o marcador da página
 * (`desenharMarcador` em assets/nnadd-configurador.js) usar as mesmas.
 * - só coordenadas: maiúsculas de 8,7 mm, ~7,5 mm por carácter (~90 mm numa
 *   latitude), linhas de base a 13 mm;
 * - coordenadas por baixo do mapa e frase: maiúsculas de 5 mm, linhas de base
 *   a 8 mm;
 * - 9 mm entre o mapa e o texto, 7 mm antes da frase;
 * - o logo (R12): 30 mm de largura, 9 mm por baixo do resto;
 * - "as duas terras": os nomes em maiúsculas de 6 mm, espaçados 0,15 da
 *   altura (encolhem se forem compridos), 2,5 mm abaixo do ponto (ou ao lado,
 *   ou por cima, se aí passar o arco: `colocarBloco`); as
 *   coordenadas a 3,4 mm, com linhas de base a 5,2 mm; a distância a 4,2 mm,
 *   4 mm para dentro do arco;
 * - frente pequena ('frente_pequena_costas'): as coordenadas com 90 mm de
 *   largura (a linha mais comprida), o logo de 30 mm por baixo;
 * - frente só com o logo ('costas'): o logo de 30 mm.
 */
export const MEDIDAS = congelar({
  maiusculasCoordenadasSoMm: 8.7,
  entrelinhaCoordenadasSoMm: 13,
  maiusculasLinhaMm: 5,
  entrelinhaLinhaMm: 8,
  espacoMapaMm: 9,
  espacoFraseMm: 7,
  logoLarguraMm: 30,
  espacoLogoMm: 9,
  maiusculasNomeMm: 6,
  espacamentoNome: 0.15,
  espacoNomeMm: 2.5,
  maiusculasPontaMm: 3.4,
  entrelinhaPontaMm: 5.2,
  espacoPontaMm: 3,
  maiusculasDistanciaMm: 4.2,
  espacoDistanciaMm: 4,
  frentePequenaLarguraMm: 90,
  frenteLogoLarguraMm: 30,
});
const MAIUSCULAS_COORDENADAS_SO_MM = MEDIDAS.maiusculasCoordenadasSoMm;
const ENTRELINHA_COORDENADAS_SO_MM = MEDIDAS.entrelinhaCoordenadasSoMm;
const MAIUSCULAS_LINHA_MM = MEDIDAS.maiusculasLinhaMm;
const ESPACO_MAPA_MM = MEDIDAS.espacoMapaMm;
const ESPACO_FRASE_MM = MEDIDAS.espacoFraseMm;
/** Os nomes compridos encolhem para caber em metade da moldura, até este mínimo. */
const MAIUSCULAS_NOME_MIN_MM = 2.6;
/** Folga à volta de cada texto onde a costa não passa (o recorte), em mm. */
const FOLGA_TEXTO_MM = 3.5;
/** "As duas terras": o vão mínimo, em mm, entre um texto e o traço do arco. */
const FOLGA_ARCO_MM = 3;
/** ... entre um texto e a borda de um ponto. */
const FOLGA_PONTO_MM = 1.5;
/** ... entre dois textos (nomes, coordenadas, distância). */
const FOLGA_ENTRE_TEXTOS_MM = 2;
/** ... entre um texto e a borda da moldura. */
const MARGEM_MOLDURA_MM = 1;

/** A distância de um ponto a uma caixa `{ x, y, w, h }` (0 dentro dela). */
function distanciaCaixaPonto(c, px, py) {
  const dx = Math.max(c.x - px, 0, px - (c.x + c.w));
  const dy = Math.max(c.y - py, 0, py - (c.y + c.h));
  return Math.hypot(dx, dy);
}

/** A distância de um ponto ao troço a–b. */
function distanciaPontoTroco(px, py, a, b) {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const l2 = dx * dx + dy * dy;
  const t = l2 ? Math.min(1, Math.max(0, ((px - a[0]) * dx + (py - a[1]) * dy) / l2)) : 0;
  return Math.hypot(px - (a[0] + t * dx), py - (a[1] + t * dy));
}

/** A distância de uma caixa ao troço a–b (0 se se tocarem). */
function distanciaCaixaTroco(c, a, b) {
  // o troço corta a caixa? (Liang–Barsky)
  let t0 = 0;
  let t1 = 1;
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const cortar = (p, q) => {
    if (p === 0) return q >= 0;
    const t = q / p;
    if (p < 0) {
      if (t > t1) return false;
      if (t > t0) t0 = t;
    } else {
      if (t < t0) return false;
      if (t < t1) t1 = t;
    }
    return true;
  };
  if (cortar(-dx, a[0] - c.x) && cortar(dx, c.x + c.w - a[0]) && cortar(-dy, a[1] - c.y) && cortar(dy, c.y + c.h - a[1])) return 0;
  const cantos = [[c.x, c.y], [c.x + c.w, c.y], [c.x, c.y + c.h], [c.x + c.w, c.y + c.h]];
  return Math.min(
    distanciaCaixaPonto(c, a[0], a[1]),
    distanciaCaixaPonto(c, b[0], b[1]),
    ...cantos.map(([x, y]) => distanciaPontoTroco(x, y, a, b)),
  );
}

// A letra das duas tabelas de glifos (IBM Plex Mono): unidades por carácter e
// altura das maiúsculas.
const AVANCO_FONTE = 600;
const MAIUSCULAS_FONTE = 698;
const SINAIS_COORDENADAS = congelar(['°', '′', '″']);

const n = (v) => String(Number(v.toFixed(3)));
const numeros = (d) => (d.match(/-?(?:\d+\.?\d*|\.\d+)(?:e[-+]?\d+)?/gi) ?? []).map(Number);

/** y mínimo e máximo de um caminho só com comandos absolutos (os pontos de controlo contam). */
function limitesY(d, escala = 1) {
  const v = numeros(d);
  let min = Infinity;
  let max = -Infinity;
  for (let i = 1; i < v.length; i += 2) {
    min = Math.min(min, v[i] * escala);
    max = Math.max(max, v[i] * escala);
  }
  return Number.isFinite(min) ? { min, max } : { min: 0, max: 0 };
}

/**
 * Cria o desenho com as dependências injetadas (KTD1). Ver o cabeçalho.
 */
export function criarDesenho(deps = {}) {
  const { mapas, glifos, formatarDMS, logotipo } = deps;
  if (!mapas || typeof mapas.desenharMapa !== 'function' || typeof mapas.mapaParaSvg !== 'function') {
    throw new Error('nnadd-configurador: falta o módulo de mapas (deps.mapas)');
  }
  if (!glifos || typeof glifos.validarFrase !== 'function' || typeof glifos.desenharFrase !== 'function') {
    throw new Error('nnadd-configurador: falta o módulo de glifos (deps.glifos)');
  }
  if (typeof formatarDMS !== 'function') throw new Error('nnadd-configurador: falta deps.formatarDMS');
  // O logo oficial vai sempre no peito (R12): sem ele não há peça.
  const vbLogo = String(logotipo?.viewBox ?? '').trim().split(/[\s,]+/).map(Number);
  if (vbLogo.length !== 4 || !vbLogo.every(Number.isFinite) || !(vbLogo[2] > 0 && vbLogo[3] > 0) || typeof logotipo.conteudo !== 'string' || !logotipo.conteudo.trim()) {
    throw new Error('nnadd-configurador: falta o logótipo oficial (deps.logotipo, LOGOTIPO de nnadd-coordinates-design.js)');
  }

  const conjuntoFrase = new Set([...(glifos.CARACTERES_FRASE ?? '')]);
  // A frase tem de usar a mesma letra: 1 carácter = 600/698 da altura das maiúsculas.
  if (Math.abs(glifos.desenharFrase('0', MAIUSCULAS_FONTE).larguraMm - AVANCO_FONTE) > 1e-6) {
    throw new Error('nnadd-configurador: os glifos da frase não têm as medidas do IBM Plex Mono esperadas');
  }
  // Os contornos de ° ′ ″ vêm da tabela da frase (U2), que não os aceita na frase.
  const sinais = glifos.CONTORNOS_SINAIS ?? {};
  for (const c of SINAIS_COORDENADAS) {
    if (!sinais[c]) throw new Error(`nnadd-configurador: faltam os contornos de ${c} nos glifos (correr npm run glyphs)`);
  }
  const ESTILOS = mapas.ESTILOS ?? [];

  // ===== Logo (R12)

  // Uma só tinta (KTD7): as cores próprias do SVG oficial saem e o logo herda a
  // tinta do grupo da peça. `fill-rule` fica.
  const conteudoLogo = logotipo.conteudo.replace(/\s(?:fill|stroke|style|class)\s*=\s*("[^"]*"|'[^']*')/gi, '');
  const escalaLogo = MEDIDAS.logoLarguraMm / vbLogo[2];
  const LOGO = congelar({ larguraMm: MEDIDAS.logoLarguraMm, alturaMm: vbLogo[3] * escalaLogo });

  /** O logo com o canto superior esquerdo em (x, y) mm; herda a tinta do grupo onde entrar. */
  function corpoLogotipo(x, y) {
    const tx = x - vbLogo[0] * escalaLogo;
    const ty = y - vbLogo[1] * escalaLogo;
    return `<g data-camada="logotipo" transform="translate(${n(tx)} ${n(ty)}) scale(${Number(escalaLogo.toPrecision(8))})">${conteudoLogo}</g>`;
  }

  // ===== Nomes dos lugares ("as duas terras")

  /**
   * O nome de um lugar como o desenho o escreve: NFC, maiúsculas, só os
   * caracteres da letra da frase (os outros saem), espaços simples, até
   * `NOME_MAX` caracteres. A página e o PC usam esta função sobre o mesmo
   * texto (o `Lugar` da encomenda), por isso dão o mesmo nome.
   */
  function nomeParaDesenho(texto) {
    if (typeof texto !== 'string') return '';
    const maiusculas = [...texto.normalize('NFC').toUpperCase().normalize('NFC')];
    const limpo = maiusculas
      .map((c) => (/\s/.test(c) ? ' ' : c))
      .filter((c) => c === ' ' || conjuntoFrase.has(c))
      .join('')
      .replace(/ +/g, ' ')
      .trim();
    return [...limpo].slice(0, NOME_MAX).join('').trim();
  }

  /** A distância para o meio do arco: km inteiros, milhares separados por espaço ("1 171 KM"). */
  function textoDistancia(km) {
    const inteiro = String(Math.max(1, Math.round(km)));
    return `${inteiro.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} KM`;
  }

  // ===== Validação

  /**
   * Valida um descritor. A mesma regra na página e no PC. Devolve
   * `{ valido, erros }`, com `erros` `{ campo: motivo }`. `opcoes.pecasAtivas`
   * substitui `PECAS_ATIVAS` (a folha de provas de U10 desenha peças sem amostra).
   */
  function validarDescritor(descritor, { pecasAtivas = PECAS_ATIVAS } = {}) {
    const erros = {};
    if (!descritor || typeof descritor !== 'object' || Array.isArray(descritor)) {
      return { valido: false, erros: { descritor: 'tipo' } };
    }
    const d = descritor;
    if (d.versao !== undefined && !VERSOES_ACEITES.includes(d.versao)) erros.versao = 'desconhecida';
    // A disposição (KTD1): em falta é 'frente'; uma conf-2 não tem outra (KTD2).
    const disposicao = d.disposicao == null ? 'frente' : DISPOSICOES.includes(d.disposicao) ? d.disposicao : null;
    if (!disposicao) erros.disposicao = 'desconhecida';
    else if (d.versao === 'conf-2' && disposicao !== 'frente') erros.disposicao = 'versao';

    const peca = dono(PECAS, d.peca) ? PECAS[d.peca] : null;
    if (!peca) erros.peca = 'desconhecida';
    else if (!pecasAtivas.includes(d.peca)) erros.peca = 'inativa';

    if (peca) {
      if (!dono(peca.cores, d.cor)) erros.cor = 'desconhecida';
      else if (!peca.cores[d.cor].tintas.includes(d.tinta)) erros.tinta = 'nao_permitida';
    }
    if (!TAMANHOS.includes(d.tamanho)) erros.tamanho = 'desconhecido';

    const composicao = COMPOSICOES.includes(d.composicao) ? d.composicao : null;
    if (!composicao) erros.composicao = 'desconhecida';
    const comMapa = COM_MAPA.includes(composicao);
    if (composicao === 'coordenadas' && d.estilo != null) erros.estilo = 'sem_mapa';
    if (comMapa) {
      if (d.estilo == null) erros.estilo = 'em_falta';
      else if (!ESTILOS.includes(d.estilo)) erros.estilo = 'desconhecido';
    }
    // Frente pequena + costas: atrás vai o mapa e a frase (R3), a composição 'mapa'.
    // Frente + costas completas: à frente uma composição com mapa (R5).
    if (composicao && disposicao === 'frente_pequena_costas' && composicao !== 'mapa') erros.composicao = 'disposicao';
    if (composicao && disposicao === 'frente_costas' && !comMapa) erros.composicao = 'disposicao';

    const pontos = Array.isArray(d.pontos) ? d.pontos : [];
    const duas = comMapa && d.estilo === 'duas_terras';
    // Em 'frente_costas', as duas terras de um dos lados pedem os dois lugares; o lado
    // com um estilo de um lugar usa o primeiro (AE2).
    const duasCostas = disposicao === 'frente_costas' && comMapa && d.estiloCostas === 'duas_terras';
    const doisLugares = duas || duasCostas;
    const valido = (p) =>
      p && typeof p.lat === 'number' && typeof p.lng === 'number' && Number.isFinite(p.lat) && Number.isFinite(p.lng) &&
      Math.abs(p.lat) <= 90 && Math.abs(p.lng) <= 180;
    const pontosDoEstilo = (estilo) => (estilo === 'duas_terras' ? pontos : pontos.slice(0, 1));
    if (duas && pontos.length < 2) erros.pontos = 'pontos_em_falta';
    else if (duasCostas && pontos.length < 2) erros.estiloCostas = 'pontos_em_falta';
    else if (doisLugares ? pontos.length !== 2 : pontos.length !== 1) erros.pontos = doisLugares ? 'dois_pontos' : 'um_ponto';
    else if (!pontos.every(valido)) erros.pontos = 'fora_do_intervalo';
    else if (comMapa && pontosDoEstilo(d.estilo).some((p) => !mapas.disponibilidadeEstilos(p.lat)[d.estilo] && ESTILOS.includes(d.estilo))) {
      erros.pontos = 'latitude';
    } else if (doisLugares) {
      const [a, b] = pontos.map((p) => ({ lat: p.lat, lon: p.lng }));
      if (mapas.distanciaM(a, b) < mapas.DISTANCIA_MINIMA_M) erros.pontos = 'pontos_proximos';
    }
    validarEstiloCostas(d, { disposicao, comMapa, pontos, pontosDoEstilo }, erros);
    if (doisLugares) {
      if (!Array.isArray(d.nomes) || d.nomes.length !== 2) erros.nomes = 'em_falta';
      else if (!d.nomes.every((x) => typeof x === 'string' && nomeParaDesenho(x) === x)) erros.nomes = 'formato';
    }

    if (d.frase !== undefined && d.frase !== null && typeof d.frase !== 'string') erros.frase = 'tipo';
    else if (peca) {
      const r = glifos.validarFrase(d.frase ?? '', peca.limiteFrase);
      if (!r.valida) erros.frase = r.motivo;
    }

    if (typeof d.qr !== 'boolean') erros.qr = 'tipo';
    else if (d.qr && peca && !peca.aceitaQR) erros.qr = 'nao_aceita';

    return { valido: Object.keys(erros).length === 0, erros };
  }

  /**
   * O estilo das costas (R5): só em 'frente_costas', outro estilo, para os mesmos lugares. Escreve
   * em `erros.estiloCostas`; corre depois da validação dos pontos (lê `erros.pontos`).
   */
  function validarEstiloCostas(d, { disposicao, comMapa, pontos, pontosDoEstilo }, erros) {
    if (disposicao && disposicao !== 'frente_costas') {
      if (d.estiloCostas != null) erros.estiloCostas = 'sem_costas';
    } else if (disposicao === 'frente_costas') {
      const e = d.estiloCostas;
      if (e == null) erros.estiloCostas = 'em_falta';
      else if (!ESTILOS.includes(e)) erros.estiloCostas = 'desconhecido';
      else if (e === d.estilo) erros.estiloCostas = 'igual';
      else if (comMapa && !erros.pontos) {
        if (e === 'duas_terras' && pontos.length < 2) erros.estiloCostas = 'pontos_em_falta';
        else if (pontosDoEstilo(e).some((p) => !mapas.disponibilidadeEstilos(p.lat)[e])) erros.estiloCostas = 'latitude';
      }
    }
  }

  // ===== Mapa

  /**
   * A forma do mapa para a composição e o estilo (null em 'coordenadas'). Em
   * 'duas_terras' depende dos dois pontos (`[{ lat, lng }]`), se vierem.
   */
  function formaMapa(composicao, estilo, pontos = null) {
    if (!COM_MAPA.includes(composicao)) return null;
    if (estilo === 'duas_terras') {
      const ok = Array.isArray(pontos) && pontos.length >= 2 && pontos.slice(0, 2).every((p) => Number.isFinite(p?.lat) && Number.isFinite(p?.lng));
      if (ok && typeof mapas.formaDuasTerras === 'function') return mapas.formaDuasTerras(pontos.slice(0, 2).map((p) => ({ lat: p.lat, lon: p.lng })));
      return { tipo: 'retangulo', ...MAPA_DUAS_TERRAS_MM };
    }
    return { tipo: 'circulo', diametroMm: MAPA_CIRCULO_MM[composicao] };
  }

  // ===== Lados (KTD1)

  const disposicaoDe = (d) => d.disposicao ?? 'frente';

  /**
   * O descritor do desenho completo de um `lado` ('frente' ou 'costas'): o
   * descritor com a composição, o estilo e a frase desse lado e `logo` (se o
   * logo fecha o desenho). null quando o lado não leva um desenho completo (a
   * frente pequena, a frente só com o logo, as costas de 'frente').
   */
  function descritorDoLado(d, lado) {
    const disposicao = disposicaoDe(d);
    // Um lado com um estilo de um lugar usa só o primeiro lugar (AE2).
    const umLugar = (x) => (x.estilo !== 'duas_terras' && Array.isArray(x.pontos) && x.pontos.length > 1 ? { ...x, pontos: x.pontos.slice(0, 1), nomes: undefined } : x);
    if (lado === 'frente') {
      return disposicao === 'frente' || disposicao === 'frente_costas' ? umLugar({ ...d, logo: true }) : null;
    }
    if (lado !== 'costas') throw new Error(`nnadd-configurador: lado desconhecido: ${JSON.stringify(lado)}`);
    if (disposicao === 'costas' || disposicao === 'frente_pequena_costas') return { ...d, logo: false };
    // Atrás, só o mapa dos mesmos lugares no outro estilo (R5).
    if (disposicao === 'frente_costas') return umLugar({ ...d, composicao: 'mapa', estilo: d.estiloCostas, frase: '', logo: false });
    return null;
  }

  /** Os lados com mapa, pela ordem frente, costas (0, 1 ou 2; KTD6). */
  function ladosComMapa(d) {
    return ['frente', 'costas'].filter((lado) => COM_MAPA.includes(descritorDoLado(d, lado)?.composicao));
  }

  /** O lado do mapa: o pedido, ou o único lado com mapa. */
  function ladoDoMapa(d, lado) {
    if (lado != null) return lado;
    const lados = ladosComMapa(d);
    if (lados.length > 1) throw new Error(`nnadd-configurador: ${disposicaoDe(d)} tem um mapa em cada lado, indicar o lado`);
    return lados[0] ?? 'frente';
  }

  /** O descritor do lado do mapa (`ladoDoMapa`), ou o próprio descritor se esse lado não tiver desenho completo. */
  const descritorDoMapa = (descritor, lado) => descritorDoLado(descritor, ladoDoMapa(descritor, lado)) ?? descritor;

  /**
   * O pedido para `mapas.desenharMapa`, a partir do descritor e da geometria
   * carregada (U4), para o mapa do `lado` (em falta: o único lado com mapa).
   */
  function pedidoMapa(descritor, dados, lado = null) {
    const d = descritorDoMapa(descritor, lado);
    const forma = formaMapa(d.composicao, d.estilo, d.pontos);
    if (!forma) throw new Error('nnadd-configurador: a composição não tem mapa');
    const pontos = d.pontos.map((p) => ({ lat: p.lat, lon: p.lng }));
    const pedido = { estilo: d.estilo, forma, dados: dados ?? {} };
    if (d.estilo === 'duas_terras') pedido.pontos = pontos.slice(0, 2);
    else pedido.centro = pontos[0];
    return pedido;
  }

  /**
   * O pedido ao carregador de dados (U4) para o estilo do mapa do `lado`
   * (em falta: o único lado com mapa, como em `pedidoMapa`): em
   * 'duas_terras', a caixa que a moldura mostra (`caixaDuasTerras`); nos
   * outros, o centro e o raio do estilo. A página e o PC pedem o mesmo.
   */
  function pedidoDados(descritor, lado = null) {
    const d = descritorDoMapa(descritor, lado);
    const { estilo } = d;
    const pontos = d.pontos.map((p) => ({ lat: p.lat, lon: p.lng }));
    if (estilo === 'duas_terras') return { estilo, caixa: mapas.caixaDuasTerras(pontos.slice(0, 2), formaMapa('mapa', estilo, d.pontos)) };
    const pedido = { estilo, centro: pontos[0] };
    const raio = mapas.RAIOS_M?.[estilo];
    if (raio !== undefined) pedido.raioM = raio;
    return pedido;
  }

  // ===== Texto

  /**
   * Uma linha de texto com as maiúsculas a `maiusculasMm`, com a origem no
   * início da linha de base. Devolve `{ corpo, larguraMm, topo, fundo }`
   * (topo e fundo em mm, relativos à linha de base). Memorizada: o resultado
   * só depende do texto e da altura, e quem o recebe só o lê.
   */
  const memoLinhas = new Map();
  function linhaTexto(texto, maiusculasMm, espacamentoMm = 0) {
    const chave = `${texto}|${maiusculasMm}|${espacamentoMm}`;
    const guardada = memoLinhas.get(chave);
    if (guardada) return guardada;
    const linha = desenharLinha(texto, maiusculasMm, espacamentoMm);
    if (memoLinhas.size >= 64) memoLinhas.delete(memoLinhas.keys().next().value);
    memoLinhas.set(chave, linha);
    return linha;
  }

  /** `espacamentoMm`: espaço a mais entre caracteres (letras espaçadas); cada carácter desenha-se à parte. */
  function desenharLinha(texto, maiusculasMm, espacamentoMm = 0) {
    const caracteres = [...String(texto).normalize('NFC')];
    const avanco = (maiusculasMm * AVANCO_FONTE) / MAIUSCULAS_FONTE + espacamentoMm;
    const escala = maiusculasMm / MAIUSCULAS_FONTE;
    const partes = [];
    let topo = 0;
    let fundo = 0;
    const juntar = (d, transform, esc) => {
      const l = limitesY(d, esc);
      topo = Math.min(topo, l.min);
      fundo = Math.max(fundo, l.max);
      partes.push(`<path transform="${transform}" d="${d}"/>`);
    };
    let i = 0;
    while (i < caracteres.length) {
      const c = caracteres[i];
      if (c === ' ') {
        i += 1;
      } else if (sinais[c] !== undefined && !conjuntoFrase.has(c)) {
        juntar(sinais[c], `translate(${n(i * avanco)} 0) scale(${Number(escala.toPrecision(8))})`, escala);
        i += 1;
      } else {
        // Uma sequência sem espaços nem sinais, desenhada pela frase (U2).
        let j = i;
        while (!espacamentoMm && j < caracteres.length && caracteres[j] !== ' ' && !(sinais[caracteres[j]] !== undefined && !conjuntoFrase.has(caracteres[j]))) j += 1;
        if (j === i) j = i + 1;
        const { d } = glifos.desenharFrase(caracteres.slice(i, j).join(''), maiusculasMm);
        if (d) juntar(d, `translate(${n(i * avanco)} 0)`, 1);
        i = j;
      }
    }
    const larguraMm = caracteres.length ? caracteres.length * avanco - espacamentoMm : 0;
    return { corpo: partes.join(''), larguraMm, topo, fundo };
  }

  // ===== Desenho

  /** Retira o `<svg>` exterior de `mapaParaSvg`, ficando o conteúdo em mm. */
  function conteudoMapa(svg) {
    const inicio = svg.indexOf('>') + 1;
    const fim = svg.lastIndexOf('</svg>');
    return svg.slice(inicio, fim);
  }

  // O conteúdo do mapa por resultado de `desenharMapa` e por tinta: a
  // pré-visualização redesenha a peça a cada tecla da frase com o mesmo mapa.
  // `recorteCosta` (duas terras): a costa não passa por baixo dos textos.
  const memoMapas = new WeakMap();
  function conteudoMapaEmCache(mapaResultado, hex, recorteCosta = null) {
    let porTinta = memoMapas.get(mapaResultado);
    if (!porTinta) {
      porTinta = new Map();
      memoMapas.set(mapaResultado, porTinta);
    }
    const chave = recorteCosta ? `${hex}|${recorteCosta}` : hex;
    let conteudo = porTinta.get(chave);
    if (conteudo === undefined) {
      const res = recorteCosta
        ? { ...mapaResultado, camadas: mapaResultado.camadas.map((c) => (c.nome === 'costa' ? { ...c, recorte: { d: recorteCosta, regra: 'evenodd' } } : c)) }
        : mapaResultado;
      conteudo = conteudoMapa(mapas.mapaParaSvg(res, { tinta: hex, prefixoId: 'nnadd-conf-mapa' }));
      if (porTinta.size >= 8) porTinta.delete(porTinta.keys().next().value);
      porTinta.set(chave, conteudo);
    }
    return conteudo;
  }

  /**
   * Os textos de "as duas terras" dentro da moldura (mm, origem no canto da
   * moldura): o nome de cada lugar junto do ponto, as coordenadas por baixo
   * do nome (só em 'coordenadas_mapa') e a distância para dentro do arco.
   * Devolve `{ corpo, caixas }`, com as caixas dos textos para o recorte da
   * costa. Memorizado por mapa e por textos (a pré-visualização redesenha a
   * cada tecla da frase).
   *
   * Cada bloco (nome e coordenadas) procura o seu lugar (`colocarBloco`):
   * por baixo do ponto, como sempre, quando aí fica livre; senão ao lado
   * (do lado contrário ao arco), por cima, um pouco mais afastado ou com o
   * nome mais pequeno, o que custar menos. Livre quer dizer: dentro da
   * moldura, a `FOLGA_ARCO_MM` do traço do arco, a `FOLGA_PONTO_MM` dos dois
   * pontos e a `FOLGA_ENTRE_TEXTOS_MM` dos outros textos.
   */
  const memoTextos = new WeakMap();
  function textosDuasTerras(d, mapaResultado) {
    const comCoordenadas = d.composicao === 'coordenadas_mapa';
    const chave = JSON.stringify([d.nomes ?? null, comCoordenadas ? d.pontos.slice(0, 2).map((p) => [p.lat, p.lng]) : null]);
    let porTextos = memoTextos.get(mapaResultado);
    if (!porTextos) {
      porTextos = new Map();
      memoTextos.set(mapaResultado, porTextos);
    }
    let r = porTextos.get(chave);
    if (!r) {
      r = calcularTextosDuasTerras(d, mapaResultado, comCoordenadas);
      if (porTextos.size >= 8) porTextos.delete(porTextos.keys().next().value);
      porTextos.set(chave, r);
    }
    return r;
  }

  function calcularTextosDuasTerras(d, mapaResultado, comCoordenadas) {
    const { extremos, apice, normal, raioPontoMm, distanciaKm } = mapaResultado.arco;
    const W = mapaResultado.larguraMm;
    const H = mapaResultado.alturaMm;
    const M = MEDIDAS;
    const partes = [];
    const caixas = [];
    const clamp = (v, min, max) => Math.min(Math.max(v, min), Math.max(min, max));
    const ambiente = ambienteDuasTerras(mapaResultado);

    // A distância, para dentro do arco a partir do ponto mais alto. Calcula-se
    // primeiro: os nomes evitam-na; se nenhum lugar livre a evitar, ela é que muda.
    const capD = M.maiusculasDistanciaMm;
    const ld = linhaTexto(textoDistancia(distanciaKm), capD, capD * M.espacamentoNome);
    const hd = ld.fundo - ld.topo;
    const caixaDistancia = (afastar, deslizar) => {
      const meio = M.espacoDistanciaMm + afastar + (Math.abs(normal[0]) * ld.larguraMm) / 2 + (Math.abs(normal[1]) * hd) / 2;
      // `deslizar` anda ao longo da corda (perpendicular à normal)
      const cx = clamp(apice[0] - normal[0] * meio - normal[1] * deslizar, ld.larguraMm / 2, W - ld.larguraMm / 2);
      const cy = clamp(apice[1] - normal[1] * meio + normal[0] * deslizar, hd / 2, H - hd / 2);
      return { x: cx - ld.larguraMm / 2, y: cy - hd / 2, w: ld.larguraMm, h: hd };
    };
    let distancia = caixaDistancia(0, 0);

    const blocos = [];
    for (const [i, qual] of ['origem', 'destino'].entries()) {
      const bloco = blocoDuasTerras(d, i, qual, W, comCoordenadas);
      if (!bloco) continue;
      const [px, py] = extremos[i];
      const outro = extremos[1 - i];
      // O alinhamento de sempre: numa corda quase vertical, para longe da
      // curva; senão, o da esquerda para a direita e o da direita para a esquerda.
      let alinhar;
      if (Math.abs(normal[0]) > 0.9) alinhar = normal[0] > 0 ? 'esq' : 'dir';
      else alinhar = px < outro[0] || (px === outro[0] && i === 0) ? 'esq' : 'dir';
      // O lado de onde sai o arco nesta ponta (para pôr o texto ao lado, do outro lado).
      const t = ambiente.tangentes[i];
      const ladoLivre = t[0] > 0 ? 'esq' : 'dir';
      blocos.push({ qual, bloco, px, py, alinhar, ladoLivre });
    }
    const colocarTodos = (comDistancia) => {
      const ocupadas = comDistancia ? [distancia] : [];
      return blocos.map((b) => {
        const c = colocarBloco(b, ocupadas, ambiente, raioPontoMm, W, H);
        ocupadas.push(c.caixa);
        return c;
      });
    };
    let colocados = colocarTodos(true);
    if (colocados.some((c) => c.excesso > 0)) {
      // Sem lugar livre à volta da distância: os nomes primeiro, a distância procura outro sítio.
      colocados = colocarTodos(false);
      let melhor = null;
      for (let afastar = 0; afastar <= 24 && !(melhor && melhor.excesso === 0); afastar += 1) {
        for (const deslizar of [0, -4, 4, -8, 8, -12, 12, -16, 16]) {
          const c = caixaDistancia(afastar, deslizar);
          const excesso = ambiente.excesso(c, colocados.map((x) => x.caixa));
          const custo = excesso * 1000 + afastar + Math.abs(deslizar) * 0.8;
          if (!melhor || custo < melhor.custo) melhor = { caixa: c, excesso, custo };
        }
      }
      distancia = melhor.caixa;
    }

    colocados.forEach((c, k) => {
      caixas.push({ ...c.caixa, texto: blocos[k].qual });
      const { caixa, linhas, pos, alinharLinhas } = c;
      linhas.forEach((x, j) => {
        const lx =
          alinharLinhas === 'esq' ? caixa.x : alinharLinhas === 'dir' ? caixa.x + caixa.w - x.l.larguraMm : caixa.x + (caixa.w - x.l.larguraMm) / 2;
        // As duas linhas das coordenadas juntam-se num só grupo por ponta.
        const atributo = x.segunda ? '' : ` data-linha="${x.nome}"`;
        partes.push(`<g${atributo} transform="translate(${n(lx)} ${n(caixa.y + pos[j])})">${x.l.corpo}</g>`);
      });
    });
    caixas.push({ ...distancia, texto: 'distancia' });
    partes.push(`<g data-linha="distancia" transform="translate(${n(distancia.x)} ${n(distancia.y - ld.topo)})">${ld.corpo}</g>`);
    return { corpo: partes.join(''), caixas };
  }

  /**
   * O bloco de texto de uma ponta, a uma escala do nome: `{ linhas, pos,
   * largura, altura, alturaNome }` (pos: a linha de base de cada linha a
   * partir do topo do bloco), ou null se a ponta não tiver texto. `escalas`
   * dá o bloco com o nome mais pequeno (as coordenadas não encolhem).
   */
  function blocoDuasTerras(d, i, qual, W, comCoordenadas) {
    const M = MEDIDAS;
    const nome = d.nomes?.[i] ?? '';
    let capNome = 0;
    if (nome) {
      const largo = (cap) => linhaTexto(nome, cap, cap * M.espacamentoNome).larguraMm;
      let cap = M.maiusculasNomeMm;
      const maximo = W / 2;
      if (largo(cap) > maximo) cap = Math.max(MAIUSCULAS_NOME_MIN_MM, (cap * maximo) / largo(cap));
      capNome = Math.floor(cap * 100) / 100;
    }
    let coordenadas = null;
    if (comCoordenadas) {
      const { latitude, longitude } = formatarDMS(d.pontos[i].lat, d.pontos[i].lng);
      coordenadas = [linhaTexto(latitude, M.maiusculasPontaMm), linhaTexto(longitude, M.maiusculasPontaMm)];
    }
    if (!nome && !coordenadas) return null;
    const montar = (cap) => {
      const linhas = [];
      if (nome) linhas.push({ nome: `nome-${qual}`, l: linhaTexto(nome, cap, cap * M.espacamentoNome), antes: 0 });
      if (coordenadas) {
        linhas.push(
          { nome: `coordenadas-${qual}`, l: coordenadas[0], antes: linhas.length ? M.espacoPontaMm : 0 },
          { nome: `coordenadas-${qual}`, l: coordenadas[1], antes: M.entrelinhaPontaMm - M.maiusculasPontaMm, segunda: true },
        );
      }
      // Empilhar: o topo de cada linha a seguir ao fundo da anterior.
      let y = 0;
      const pos = linhas.map((x) => {
        y += x.antes;
        const base = y - x.l.topo;
        y = base + x.l.fundo;
        return base;
      });
      return {
        linhas,
        pos,
        largura: Math.max(...linhas.map((x) => x.l.larguraMm)),
        altura: y,
        alturaNome: linhas[0].l.fundo - linhas[0].l.topo,
        cap,
      };
    };
    // As escalas do nome a experimentar, da maior para a menor (6 % de cada vez).
    const escalas = [];
    if (capNome) {
      for (let k = 0; k < 8; k++) {
        const cap = Math.max(MAIUSCULAS_NOME_MIN_MM, Math.floor(capNome * (1 - 0.06 * k) * 100) / 100);
        if (!escalas.length || escalas[escalas.length - 1].cap !== cap) escalas.push(montar(cap));
      }
    } else escalas.push(montar(0));
    return { escalas, capInicial: capNome };
  }

  /**
   * O que um texto tem de evitar em "as duas terras": o arco (a linha do
   * mapa, com a espessura), os dois pontos e a moldura. `excesso(caixa,
   * outras)` soma quanto (em mm) a caixa entra nas folgas: 0 = livre.
   */
  function ambienteDuasTerras(mapaResultado) {
    const W = mapaResultado.larguraMm;
    const H = mapaResultado.alturaMm;
    const { extremos, raioPontoMm } = mapaResultado.arco;
    const camada = (mapaResultado.camadas ?? []).find((c) => c.nome === 'arco');
    const v = numeros(camada?.d ?? '');
    const arco = [];
    for (let i = 0; i + 1 < v.length; i += 2) arco.push([v[i], v[i + 1]]);
    if (arco.length < 2) arco.splice(0, arco.length, extremos[0], extremos[1]);
    const meioTraco = (camada?.espessuraMm ?? 0) / 2;
    const unit = (a, b) => {
      const dx = b[0] - a[0];
      const dy = b[1] - a[1];
      const l = Math.hypot(dx, dy) || 1;
      return [dx / l, dy / l];
    };
    // A direção do arco a sair de cada ponta (a uns 5 % do comprimento).
    const k = Math.max(1, Math.round(arco.length / 20));
    const tangentes = [unit(arco[0], arco[Math.min(k, arco.length - 1)]), unit(arco[arco.length - 1], arco[Math.max(0, arco.length - 1 - k)])];
    const ax0 = Math.min(...arco.map((p) => p[0]));
    const ax1 = Math.max(...arco.map((p) => p[0]));
    const ay0 = Math.min(...arco.map((p) => p[1]));
    const ay1 = Math.max(...arco.map((p) => p[1]));
    const folgaArco = meioTraco + FOLGA_ARCO_MM;

    function excesso(c, outras = []) {
      let e = 0;
      const m = MARGEM_MOLDURA_MM;
      e += Math.max(0, m - c.x) + Math.max(0, c.x + c.w - (W - m)) + Math.max(0, m - c.y) + Math.max(0, c.y + c.h - (H - m));
      // o arco (só os troços perto da caixa)
      if (!(c.x - folgaArco > ax1 || c.x + c.w + folgaArco < ax0 || c.y - folgaArco > ay1 || c.y + c.h + folgaArco < ay0)) {
        let dmin = Infinity;
        for (let i = 1; i < arco.length && dmin > 0; i++) dmin = Math.min(dmin, distanciaCaixaTroco(c, arco[i - 1], arco[i]));
        e += Math.max(0, folgaArco - dmin);
      }
      for (const p of extremos) e += Math.max(0, raioPontoMm + FOLGA_PONTO_MM - distanciaCaixaPonto(c, p[0], p[1]));
      for (const o of outras) {
        const vao = Math.max(o.x - (c.x + c.w), c.x - (o.x + o.w), o.y - (c.y + c.h), c.y - (o.y + o.h));
        e += Math.max(0, FOLGA_ENTRE_TEXTOS_MM - vao);
      }
      return e;
    }
    return { excesso, tangentes };
  }

  /**
   * O lugar de um bloco de texto junto do seu ponto: o de menor custo entre
   * as posições candidatas (por baixo, ao lado, por cima; afastadas do ponto
   * de 0 a 30 mm; com o nome até ~40 % mais pequeno) que fiquem livres
   * (`excesso` 0). Sem nenhuma livre, a que menos entra nas folgas.
   * Devolve `{ caixa, linhas, pos, alinharLinhas, excesso }`.
   */
  function colocarBloco({ bloco, px, py, alinhar, ladoLivre }, ocupadas, ambiente, r, W, H) {
    const g = MEDIDAS.espacoNomeMm;
    const m = MARGEM_MOLDURA_MM;
    const outro = (a) => (a === 'esq' ? 'dir' : 'esq');
    // [onde, alinhamento, custo base]: por baixo como sempre custa 0.
    const ancoras = [
      ['baixo', alinhar, 0],
      ['lado', ladoLivre, 3],
      ['baixo', 'centro', 3],
      ['baixo', outro(alinhar), 4],
      ['cima', alinhar, 5],
      ['cima', 'centro', 6],
      ['cima', outro(alinhar), 7],
      ['lado', outro(ladoLivre), 10],
    ];
    const caixaDe = (e, onde, al, afastar) => {
      const { largura: w, altura: h } = e;
      if (onde === 'lado') {
        const x = al === 'dir' ? px + r + g + afastar : px - r - g - w - afastar;
        // o nome centrado na altura do ponto
        const y = Math.min(Math.max(py - e.alturaNome / 2, m), H - m - h);
        return { x, y, w, h };
      }
      const x0 = al === 'esq' ? px - r : al === 'dir' ? px + r - w : px - w / 2;
      const x = Math.min(Math.max(x0, m), Math.max(m, W - m - w));
      const y = onde === 'baixo' ? py + r + g + afastar : py - r - g - h - afastar;
      return { x, y, w, h };
    };
    let melhor = null;
    let reserva = null;
    for (const [onde, al, base] of ancoras) {
      if (melhor && base >= melhor.custo) break;
      bloco.escalas.forEach((e, k) => {
        const encolher = bloco.capInicial ? ((bloco.capInicial - e.cap) / bloco.capInicial) * 100 * 0.35 : 0;
        if (melhor && base + encolher >= melhor.custo) return;
        for (let afastar = 0; afastar <= 30; afastar += 0.5) {
          const custo = base + encolher + afastar * 1.5;
          if (melhor && custo >= melhor.custo) break;
          const caixa = caixaDe(e, onde, al, afastar);
          const excesso = ambiente.excesso(caixa, ocupadas);
          const alinharLinhas = onde === 'lado' ? (al === 'dir' ? 'esq' : 'dir') : al;
          if (excesso === 0) {
            melhor = { caixa, linhas: e.linhas, pos: e.pos, alinharLinhas, excesso, custo };
            break;
          }
          if (!reserva || excesso < reserva.excesso) reserva = { caixa, linhas: e.linhas, pos: e.pos, alinharLinhas, excesso, custo };
        }
      });
    }
    return melhor ?? reserva;
  }

  /**
   * As caixas (mm, na moldura do mapa) dos textos de "as duas terras" para o
   * descritor do lado do mapa: `[{ texto: 'origem' | 'destino' | 'distancia',
   * x, y, w, h }]`. Para os testes (e quem precise de saber onde ficaram).
   */
  function caixasDuasTerras(descritor, mapaResultado, lado = null) {
    const d = descritorDoMapa(descritor, lado);
    return textosDuasTerras(d, mapaResultado).caixas.map((c) => ({ ...c }));
  }

  /** Lança se o descritor for inválido. */
  function exigirValido(descritor, opcoes) {
    const v = validarDescritor(descritor, opcoes);
    if (!v.valido) {
      const lista = Object.entries(v.erros).map(([k, m]) => `${k}: ${m}`).join(', ');
      throw new Error(`nnadd-configurador: descritor inválido (${lista})`);
    }
  }

  /** O logo como elemento da pilha. */
  const elementoLogo = (espacoAntes) => ({ largura: LOGO.larguraMm, altura: LOGO.alturaMm, espacoAntes, corpo: (x, y) => corpoLogotipo(x, y) });

  /**
   * Linhas centradas umas por baixo das outras, com as maiúsculas a
   * `maiusculas` e as linhas de base a `entrelinha`, como um só elemento.
   */
  function elementoLinhas(linhas, maiusculas, entrelinha) {
    const ls = linhas.map(([nome, texto]) => ({ nome, l: linhaTexto(texto, maiusculas) }));
    const topo = ls[0].l.topo;
    const fundo = entrelinha * (ls.length - 1) + ls[ls.length - 1].l.fundo;
    return {
      largura: Math.max(...ls.map((x) => x.l.larguraMm)),
      altura: fundo - topo,
      espacoAntes: 0,
      corpo: (x, y, largura) =>
        ls
          .map(({ nome, l }, i) => `<g data-linha="${nome}" transform="translate(${n(x + (largura - l.larguraMm) / 2)} ${n(y - topo + i * entrelinha)})">${l.corpo}</g>`)
          .join(''),
    };
  }

  /**
   * O desenho completo de um lado (o desenho de hoje), como pilha de
   * elementos: mapa, coordenadas, frase e, se `d.logo`, o logo por baixo de
   * tudo (R12). `d` é o descritor do lado (`descritorDoLado`).
   */
  function elementosCompletos(d, mapaResultado) {
    const peca = PECAS[d.peca];
    const hex = TINTAS[d.tinta].hex.toLowerCase();
    const comMapa = COM_MAPA.includes(d.composicao);
    const elementos = []; // { largura, topo, altura, corpo(x, y) } empilhados de cima para baixo

    if (comMapa && mapaResultado?.espera) {
      const forma = formaMapa(d.composicao, d.estilo, d.pontos);
      const circulo = forma.tipo === 'circulo';
      const largura = circulo ? forma.diametroMm : forma.larguraMm;
      const altura = circulo ? forma.diametroMm : forma.alturaMm;
      const cheio = circulo
        ? `<circle cx="${n(largura / 2)}" cy="${n(altura / 2)}" r="${n(largura / 2)}" fill="${hex}" fill-opacity="0.16"/>`
        : `<rect width="${n(largura)}" height="${n(altura)}" fill="${hex}" fill-opacity="0.16"/>`;
      elementos.push({
        largura,
        altura,
        espacoAntes: 0,
        corpo: (x, y) => `<g data-camada="mapa-espera" transform="translate(${n(x)} ${n(y)})">${cheio}</g>`,
      });
    } else if (comMapa) {
      if (!mapaResultado) throw new Error('nnadd-configurador: falta o mapa desta composição');
      if (mapaResultado.estilo !== d.estilo) throw new Error(`nnadd-configurador: o mapa é de outro estilo (${mapaResultado.estilo})`);
      if (!mapaResultado.disponivel) throw new Error(`nnadd-configurador: mapa não disponível (${mapaResultado.motivo})`);
      if (mapaResultado.vazio) throw new Error('nnadd-configurador: o mapa está vazio (R20, KTD12)');
      const forma = formaMapa(d.composicao, d.estilo, d.pontos);
      const largura = forma.tipo === 'circulo' ? forma.diametroMm : forma.larguraMm;
      const altura = forma.tipo === 'circulo' ? forma.diametroMm : forma.alturaMm;
      if (Math.abs(mapaResultado.larguraMm - largura) > 0.01 || Math.abs(mapaResultado.alturaMm - altura) > 0.01) {
        throw new Error('nnadd-configurador: o mapa tem outro tamanho (usar pedidoMapa)');
      }
      let textos = '';
      let recorte = null;
      if (d.estilo === 'duas_terras') {
        if (!mapaResultado.arco?.extremos) throw new Error('nnadd-configurador: o mapa de "as duas terras" não traz as pontas do arco');
        const t = textosDuasTerras(d, mapaResultado);
        textos = t.corpo;
        const f = FOLGA_TEXTO_MM;
        const r = (v) => String(Math.round(v * 100) / 100);
        recorte =
          `M0 0L${r(largura)} 0L${r(largura)} ${r(altura)}L0 ${r(altura)}Z` +
          t.caixas.map((c) => `M${r(c.x - f)} ${r(c.y - f)}L${r(c.x + c.w + f)} ${r(c.y - f)}L${r(c.x + c.w + f)} ${r(c.y + c.h + f)}L${r(c.x - f)} ${r(c.y + c.h + f)}Z`).join('');
      }
      const conteudo = conteudoMapaEmCache(mapaResultado, hex, recorte);
      elementos.push({
        largura,
        altura,
        espacoAntes: 0,
        corpo: (x, y) => `<g data-camada="mapa" transform="translate(${n(x)} ${n(y)})">${conteudo}${textos}</g>`,
      });
    }

    const linha = (nome, texto, maiusculas, espacoAntes) => {
      const l = linhaTexto(texto, maiusculas);
      elementos.push({
        largura: l.larguraMm,
        altura: l.fundo - l.topo,
        espacoAntes,
        corpo: (x, y) => `<g data-linha="${nome}" transform="translate(${n(x)} ${n(y - l.topo)})">${l.corpo}</g>`,
      });
    };

    if (d.composicao === 'coordenadas') {
      const { latitude, longitude } = formatarDMS(d.pontos[0].lat, d.pontos[0].lng);
      // A segunda linha de base fica a ENTRELINHA da primeira.
      elementos.push(elementoLinhas([['latitude', latitude], ['longitude', longitude]], MAIUSCULAS_COORDENADAS_SO_MM, ENTRELINHA_COORDENADAS_SO_MM));
    } else if (d.composicao === 'coordenadas_mapa' && d.estilo !== 'duas_terras') {
      // Em "as duas terras" as coordenadas já estão junto das pontas.
      const p = d.pontos[0];
      linha('coordenadas', formatarDMS(p.lat, p.lng).texto, MAIUSCULAS_LINHA_MM, ESPACO_MAPA_MM);
    }

    const frase = glifos.validarFrase(d.frase ?? '', peca.limiteFrase).frase;
    const soMapaAntes = d.composicao === 'mapa' || d.estilo === 'duas_terras';
    if (frase) linha('frase', frase, MAIUSCULAS_LINHA_MM, elementos.length ? (soMapaAntes && comMapa ? ESPACO_MAPA_MM : ESPACO_FRASE_MM) : 0);

    // O logo, centrado por baixo de tudo (R12), no lado que o leva (R6).
    if (d.logo) elementos.push(elementoLogo(MEDIDAS.espacoLogoMm));
    return elementos;
  }

  /**
   * A frente pequena de 'frente_pequena_costas' (R3): as coordenadas do lugar
   * (latitude e longitude em duas linhas) ou dos dois lugares (uma linha
   * cada), com a linha mais comprida a `MEDIDAS.frentePequenaLarguraMm`, e o
   * logo por baixo.
   */
  function elementosFrentePequena(d) {
    let linhas;
    if (d.pontos.length === 1) {
      const { latitude, longitude } = formatarDMS(d.pontos[0].lat, d.pontos[0].lng);
      linhas = [['latitude', latitude], ['longitude', longitude]];
    } else {
      linhas = d.pontos.slice(0, 2).map((p, i) => [`coordenadas-${i ? 'destino' : 'origem'}`, formatarDMS(p.lat, p.lng).texto]);
    }
    const caracteres = Math.max(...linhas.map(([, t]) => [...t].length));
    // A altura das maiúsculas que dá a largura pedida (arredondada para baixo ao centésimo).
    const maiusculas = Math.floor(((MEDIDAS.frentePequenaLarguraMm * MAIUSCULAS_FONTE) / (AVANCO_FONTE * caracteres)) * 100) / 100;
    const entrelinha = (maiusculas * ENTRELINHA_COORDENADAS_SO_MM) / MAIUSCULAS_COORDENADAS_SO_MM;
    return [elementoLinhas(linhas, maiusculas, entrelinha), elementoLogo(MEDIDAS.espacoLogoMm)];
  }

  /** Empilha os elementos, centrados na largura maior, num SVG em mm numa só tinta. */
  function compor(elementos, hex, versao) {
    const largura = Math.max(...elementos.map((e) => e.largura));
    let y = 0;
    const corpo = elementos
      .map((e, i) => {
        if (i > 0) y += e.espacoAntes;
        const s = e.corpo((largura - e.largura) / 2, y, e.largura);
        y += e.altura;
        return s;
      })
      .join('');
    const W = n(largura);
    const H = n(y);
    return (
      `<svg xmlns="http://www.w3.org/2000/svg" width="${W}mm" height="${H}mm" viewBox="0 0 ${W} ${H}" data-desenho="${versao}">` +
      `<g fill="${hex}">${corpo}</g></svg>`
    );
  }

  /**
   * Desenha os dois lados da peça (KTD1): `{ frente, costas }`, cada um um SVG
   * em milímetros numa só tinta (KTD7), `costas` null em 'frente'. O logo vai
   * uma vez, sempre à frente (R6). `mapasLados` é `{ frente?, costas? }`, a
   * saída de `mapas.desenharMapa(pedidoMapa(descritor, dados, lado))` para
   * cada lado de `ladosComMapa`: tem de estar disponível, não vazia, do estilo
   * desse lado e do tamanho de `formaMapa`. Lança se o descritor for
   * inválido. O SVG leva em `data-desenho` a versão do descritor (KTD2). O
   * mesmo descritor dá sempre os mesmos SVG.
   */
  function desenharLados(descritor, mapasLados = {}, opcoes = {}) {
    exigirValido(descritor, opcoes);
    return desenharLadosValidos(descritor, mapasLados);
  }

  /** `desenharLados` sem a validação, para quem já validou o descritor. */
  function desenharLadosValidos(descritor, mapasLados) {
    const d = descritor;
    const hex = TINTAS[d.tinta].hex.toLowerCase();
    const versao = d.versao ?? VERSAO_CONFIGURADOR;
    const disposicao = disposicaoDe(d);
    const lados = mapasLados ?? {};
    let frente;
    if (disposicao === 'frente_pequena_costas') frente = elementosFrentePequena(d);
    else if (disposicao === 'costas') frente = [elementoLogo(0)];
    else frente = elementosCompletos(descritorDoLado(d, 'frente'), lados.frente);
    const ladoCostas = descritorDoLado(d, 'costas');
    return {
      frente: compor(frente, hex, versao),
      costas: ladoCostas ? compor(elementosCompletos(ladoCostas, lados.costas), hex, versao) : null,
    };
  }

  /**
   * Desenha a frente da disposição 'frente' (o desenho de hoje): um SVG em
   * milímetros, numa só tinta (KTD7), com o logo pequeno por baixo de tudo
   * (R12). `mapaResultado` é a saída de `mapas.desenharMapa(pedidoMapa(...))`,
   * obrigatória nas composições com mapa. As outras disposições desenham-se
   * com `desenharLados`. Lança se o descritor for inválido.
   */
  function desenharPeca(descritor, mapaResultado, opcoes = {}) {
    exigirValido(descritor, opcoes);
    if (disposicaoDe(descritor) !== 'frente') {
      throw new Error(`nnadd-configurador: a disposição ${descritor.disposicao} tem dois lados (usar desenharLados)`);
    }
    return desenharLadosValidos(descritor, { frente: mapaResultado }).frente;
  }

  return {
    validarDescritor,
    formaMapa,
    pedidoMapa,
    pedidoDados,
    descritorDoLado,
    ladosComMapa,
    nomeParaDesenho,
    textoDistancia,
    caixasDuasTerras,
    corpoLogotipo,
    logotipo: LOGO,
    desenharLados,
    desenharPeca,
    transicaoPeca,
    mudarCor,
    tintasPermitidas,
    tintaPorOmissao,
  };
}
